import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import api from '../../utils/api';
import { useTenant } from '../../context/TenantContext';

const TasksKanban = () => {
  const { activeTenant } = useTenant();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [createData, setCreateData] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium',
  });
  const [editData, setEditData] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium',
    status: 'En proceso',
  });
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  const columns = ['En proceso', 'Pendiente cliente', 'Validando', 'Exportado', 'Cerrado'];
  const statusLabels = {
    'En proceso': 'En proceso',
    'Pendiente cliente': 'Pendiente cliente',
    Validando: 'Validando',
    Exportado: 'Exportado',
    Cerrado: 'Cerrado',
  };
  const priorityLabels = {
    low: 'Baja',
    medium: 'Media',
    high: 'Alta',
  };

  const loadTasks = async () => {
    if (!activeTenant) return;
    try {
      setLoading(true);
      const res = await api.get(`/tasks?tenant_id=${activeTenant}`);
      setTasks(res.data || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
      toast.error('Error al cargar tareas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTenant) {
      loadTasks();
    }
  }, [activeTenant]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!activeTenant) return;
    try {
      await api.post('/tasks', {
        tenant_id: activeTenant,
        title: createData.title,
        description: createData.description,
        due_date: new Date(createData.due_date).toISOString(),
        priority: createData.priority,
      });
      toast.success('Tarea creada');
      setCreateOpen(false);
      setCreateData({ title: '', description: '', due_date: '', priority: 'medium' });
      loadTasks();
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Error al crear la tarea');
    }
  };

  const openEdit = (task) => {
    setSelectedTask(task);
    setEditData({
      title: task.title || '',
      description: task.description || '',
      due_date: task.due_date ? task.due_date.slice(0, 10) : '',
      priority: task.priority || 'medium',
      status: task.status || 'En proceso',
    });
    setEditOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedTask) return;
    try {
      await api.put(`/tasks/${selectedTask.id}`, {
        title: editData.title,
        description: editData.description,
        priority: editData.priority,
        status: editData.status,
      });
      toast.success('Tarea actualizada');
      setEditOpen(false);
      setSelectedTask(null);
      loadTasks();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Error al actualizar la tarea');
    }
  };

  const handleMove = async (task, status) => {
    try {
      await api.put(`/tasks/${task.id}`, { status });
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? { ...t, status } : t))
      );
      toast.success('Tarea movida');
    } catch (error) {
      console.error('Error moving task:', error);
      toast.error('Error al mover la tarea');
    }
  };

  const filteredTasks = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return tasks.filter((task) => {
      const matchesQuery =
        !normalized ||
        task.title?.toLowerCase().includes(normalized) ||
        task.description?.toLowerCase().includes(normalized);
      const matchesStatus = statusFilter === 'all' ? true : task.status === statusFilter;
      const matchesPriority = priorityFilter === 'all' ? true : task.priority === priorityFilter;
      return matchesQuery && matchesStatus && matchesPriority;
    });
  }, [tasks, query, statusFilter, priorityFilter]);

  const counts = useMemo(() => {
    const data = {};
    columns.forEach((col) => {
      data[col] = filteredTasks.filter((t) => t.status === col).length;
    });
    return data;
  }, [filteredTasks]);

  return (
    <div data-testid="tasks-kanban">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-4xl font-heading font-bold">Tareas - Kanban</h1>
          <p className="text-muted-foreground">Arrastra y gestiona prioridades</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-task-button">Nueva Tarea</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nueva Tarea</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label>Titulo</Label>
                <Input
                  value={createData.title}
                  onChange={(e) => setCreateData({ ...createData, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Descripcion</Label>
                <Textarea
                  value={createData.description}
                  onChange={(e) => setCreateData({ ...createData, description: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Fecha limite</Label>
                <Input
                  type="date"
                  value={createData.due_date}
                  onChange={(e) => setCreateData({ ...createData, due_date: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Prioridad</Label>
                <Select
                  value={createData.priority}
                  onValueChange={(value) => setCreateData({ ...createData, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{priorityLabels.low}</SelectItem>
                    <SelectItem value="medium">{priorityLabels.medium}</SelectItem>
                    <SelectItem value="high">{priorityLabels.high}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full">Crear</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <Input
            placeholder="Buscar por titulo o descripcion"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="max-w-xs"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value="all">Todos los estados</option>
            {columns.map((status) => (
              <option key={status} value={status}>{statusLabels[status]}</option>
            ))}
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value="all">Todas las prioridades</option>
            <option value="low">Baja</option>
            <option value="medium">Media</option>
            <option value="high">Alta</option>
          </select>
          <div className="ml-auto text-sm text-muted-foreground">
            {loading ? 'Actualizando...' : `${filteredTasks.length} tareas`}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {columns.map((col) => (
          <div key={col}>
            <Card className="h-full">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center justify-between">
                  <span>{statusLabels[col] || col}</span>
                  <span className="text-xs text-muted-foreground">{counts[col] || 0}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {filteredTasks.filter((t) => t.status === col).map((task) => (
                  <div
                    key={task.id}
                    className="p-3 border rounded-lg hover:border-primary cursor-pointer"
                    onClick={() => openEdit(task)}
                    data-testid={`task-${task.id}`}
                  >
                    <div className="font-medium text-sm mb-1">{task.title}</div>
                    <div className="flex items-center gap-2 justify-between">
                      <Badge
                        variant={task.priority === 'high' ? 'destructive' : 'secondary'}
                        className="text-xs"
                      >
                        {priorityLabels[task.priority] || task.priority}
                      </Badge>
                      <Select
                        value={task.status}
                        onValueChange={(value) => handleMove(task, value)}
                      >
                        <SelectTrigger className="h-7 w-28 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {columns.map((status) => (
                            <SelectItem key={status} value={status}>
                              {statusLabels[status] || status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        ))}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Tarea</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <Label>Titulo</Label>
              <Input
                value={editData.title}
                onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Descripcion</Label>
              <Textarea
                value={editData.description}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Prioridad</Label>
              <Select
                value={editData.priority}
                onValueChange={(value) => setEditData({ ...editData, priority: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">{priorityLabels.low}</SelectItem>
                  <SelectItem value="medium">{priorityLabels.medium}</SelectItem>
                  <SelectItem value="high">{priorityLabels.high}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Estado</Label>
              <Select
                value={editData.status}
                onValueChange={(value) => setEditData({ ...editData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  {columns.map((status) => (
                    <SelectItem key={status} value={status}>
                      {statusLabels[status] || status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full">Guardar cambios</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default TasksKanban;
